import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { CourseId } from '@/openapi/enum';

import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyCalcMixin from '@/user/mixins/StudyCalcMixin';
import UserMixin from '@/user/mixins/UserMixin';

import Header from '@/user/components/header/Header.vue';
import BackButton from '@/user/components/header/backButton/BackButton.vue';
import ClearButton from '@/user/components/header/clearButton/ClearButton.vue';
import ScoreButton from '@/user/components/header/scoreButton/ScoreButton.vue';
import Calc1Confirm from '@/user/pages/authorized/study/calc/calc1/confirm/Calc1Confirm.vue';
import Calc1Work from '@/user/pages/authorized/study/calc/calc1/work/Calc1Work.vue';
import RestErrorBar from '@/user/components/study/restErrorBar/RestErrorBar.vue';
import RestTimeBar from '@/user/components/study/restTimeBar/RestTimeBar.vue';
import RestQuestionCount from '@/user/components/study/restQuestionCount/RestQuestionCount.vue';
import ConfirmDialog from '@/user/components/dialog/confirm/ConfirmDialog.vue';
import StudyResultDialog from '@/user/components/study/dialog/result/StudyResultDialog.vue';



/** 認識モード（整数・小数、あまり有り、分数） */
enum CALC_RECOG_MODE {
  C_DEFAULT,
  C_REST,
  C_FRACTION,
}

/** 入力確認欄位置（整数、分子/あまり、分母） */
enum CONFIRM_POSITION {
  REGULAR,
  NUMERATOR,
  DENOMINATOR
};

/** 入力確認欄定義 */
type Confirm = {
  index: number;
  input: string;
  position: CONFIRM_POSITION
};

/** お手つき上限 */
const INCORRECT_LIMIT = 3;

export default mixins(RoutesMixin, StudyCalcMixin, UserMixin).extend({

  name: 'Calc1',

  components: {
    Header,
    BackButton,
    ClearButton,
    ScoreButton,
    RestErrorBar,
    RestTimeBar,
    RestQuestionCount,
    ConfirmDialog,
    StudyResultDialog,
    Calc1Confirm,
    Calc1Work
  },

  data(): {
    recogMode: CALC_RECOG_MODE;
    visibleBackDialog: boolean;
    confirms: Confirm[];
    focusIndex: number;
    clearSwitch: boolean; // workでwatchして全消去処理を行うためだけのトグルスイッチ
  } {
    return {
      recogMode: CALC_RECOG_MODE.C_DEFAULT,
      visibleBackDialog: false,
      confirms: [],
      focusIndex: 0,
      clearSwitch: true,
    };
  },

  computed: {
    isDispRecogCRest(): boolean {
      return this.recogMode === CALC_RECOG_MODE.C_REST;
    },
    isDispVinculum(): boolean {
      return this.recogMode === CALC_RECOG_MODE.C_FRACTION;
    },
    hasEmpty(): boolean {
      return _.some(this.confirms, c => c.input.length === 0);
    },
    regularConfirms(): Confirm[] {
      return _.filter(this.confirms, c => c.position === CONFIRM_POSITION.REGULAR);
    },
    numeratorConfirms(): Confirm[] {
      return _.filter(this.confirms, c => c.position === CONFIRM_POSITION.NUMERATOR);
    },
    denominatorConfirms(): Confirm[] {
      return _.filter(this.confirms, c => c.position === CONFIRM_POSITION.DENOMINATOR);
    },
  },

  async created(): Promise<void> {
    // font読み込み
    await this.loadFont();

    this.$_initialize();
  },

  destroyed(): void {
    this.clearRestTimeInterval();
    this.clearStudyVariable();
    this.clearStudyResult();
  },

  methods: {
    back(): void {
      this.visibleBackDialog = false;
      this.$router.push(this.routes.UnitSelect);
    },
    clear(): void {
      this.confirms.forEach(c => c.input = '');
      this.focusIndex = 0;
      this.clearSwitch = !this.clearSwitch;
    },
    score(): void {
      // 問題読み込み前は何もしない
      if (!this.isQuestionLoadCompelete) return;

      // 採点処理
      let isCorrect = true;
      if (!this.isAlwaysCorrect) isCorrect = this.$_judge(); // 正解ユーザー以外は答え合わせ

      // 不正解回数の更新
      if (!this.isTutorial && !this.hasEmpty && !isCorrect) this.incrementIncorretCount();

      // 学習メモの更新
      if (!isCorrect) this.addStudyMemo(String(this.question!.questionNo));

      // 学習結果指定、結果モーダルを開く
      this.judgeScoreResult(this.hasEmpty, isCorrect, INCORRECT_LIMIT);
    },
    input(char: string): void {
      // 入力文字格納
      this.confirms[this.focusIndex].input = char;

      // 次の未入力欄へフォーカス
      this.$_setNextFocus();
    },
    focus(num: number): void {
      this.focusIndex = num;
    },

    // StudyMixin上書き
    nextQuestion(): void {
      this.forwardQuestionIndex();
      this.$_setConfirms();
      this.startTimer();
    },

    // StudyMixinを上書き、学習終了後に学習続行用
    initializeCourseComponent(): void {
      this.initializeStudyVariable();
      this.$_initialize();
    },

    async $_initialize(): Promise<void> {
      // 学習進度読み込み
      await this.loadProgressesState(CourseId.calc);

      // 初期化
      this.initializeStudyVariable();
      this.initializeStudyRequest();
      this.recogMode         = CALC_RECOG_MODE.C_DEFAULT;
      this.visibleBackDialog = false;
      this.confirms          = [];
      this.focusIndex        = 0;
      // this.clearSwitch       = true; // 初期化しない

      // 回答可能回数
      this.setIncorretLimit(INCORRECT_LIMIT);

      // ステージ名設定
      const stageName = this.createStageName();
      this.setStageName(stageName);

      // 問題取得
      if (this.isJudgeUnit())
        await this.loadJudgeQuestions(); // 判定モード
      else
        await this.loadQuestions();      // 判定モード以外

      // 問題加工
      this.$_calcRight();       // 解答算出
      this.addQuestionImages(); // 画像URL設定

      // 問題開始
      this.$_startQuestion();
    },
    $_startQuestion(): void {
      this.initializeQuestionIndex();
      this.nextQuestion();
    },
    $_getInput(): string {
      let ret = '';

      // 整数・小数
      if (this.recogMode === CALC_RECOG_MODE.C_DEFAULT) {
        ret = this.confirms.map(c => c.input).join('');

      // 自然数あまり有り
      } else if (this.recogMode === CALC_RECOG_MODE.C_REST) {
        let s1 = this.regularConfirms.map(c => c.input).join('');   // 商
        let s2 = this.numeratorConfirms.map(c => c.input).join(''); // あまり
        ret = s2.length > 0 ? `${s1}$${s2}` : s1;

      // 分数
      } else if (this.recogMode === CALC_RECOG_MODE.C_FRACTION) {
        let s1 = this.regularConfirms.map(c => c.input).join('');     // 整数部
        let s2 = this.numeratorConfirms.map(c => c.input).join('');   // 分子
        let s3 = this.denominatorConfirms.map(c => c.input).join(''); // 分母
        if (s2.length === 0 && s3.length === 0) {
          ret = s1;
        } else {
          if (s1.length > 0) ret = s1 + ' ';
          ret += `${s2}#${s3}`;
        }
      }

      return ret;
    },
    $_setConfirms(): void {
      if (this.question === null) throw new Error('no question'); // 問題がない場合はthrow

      const right = this.question.right;
      if      (right.indexOf('$') !== -1) this.recogMode = CALC_RECOG_MODE.C_REST;
      else if (right.indexOf('#') !== -1) this.recogMode = CALC_RECOG_MODE.C_FRACTION;
      else                                this.recogMode = CALC_RECOG_MODE.C_DEFAULT;

      // 解答欄初期化
      this.confirms = [];

      // まだ回答が設定されていない場合はここで終了
      if (this.question.right.length === 0) return;

      // 整数・小数
      if (this.recogMode === CALC_RECOG_MODE.C_DEFAULT) {
        for (let i = 0; i < right.length; i++) {
          this.confirms.push({ index: i, input: '', position: CONFIRM_POSITION.REGULAR });
        }

      // あまり有り
      } else if (this.recogMode === CALC_RECOG_MODE.C_REST) {
        const pos = right.indexOf('$');
        const s1  = right.substring(0, pos);  // 商
        const s2  = right.substring(pos + 1); // あまり

        let index = 0;
        // 商
        for (let i = 0; i < s1.length; i++) {
          this.confirms.push({ index: index, input: '', position: CONFIRM_POSITION.REGULAR });
          index++;
        }
        // あまり
        for (let i = 0; i < s2.length; i++) {
          this.confirms.push({ index: index, input: '', position: CONFIRM_POSITION.NUMERATOR });
          index++;
        }

      // 分数
      } else if (this.recogMode === CALC_RECOG_MODE.C_FRACTION) {
        let r  = right;
        let s1 = ''; // 整数部
        let s2 = ''; // 分子
        let s3 = ''; // 分母
        let pos = r.indexOf(' ');
        if (pos > 0) {
          s1 = r.substring(0, pos);
          r  = r.substring(pos + 1);
        }
        pos = r.indexOf('#');
        s2  = r.substring(0, pos);
        s3  = r.substring(pos + 1);

        let index = 0;
        // 整数
        for (let i = 0; i < s1.length; i++) {
          this.confirms.push({ index: index, input: '', position: CONFIRM_POSITION.REGULAR });
          index++;
        }
        // 分母（フォーカスの順番を考慮して分母からセットする）
        for (let i = 0; i < s3.length; i++) {
          this.confirms.push({ index: index, input: '', position: CONFIRM_POSITION.DENOMINATOR });
          index++;
        }
        // 分子
        for (let i = 0; i < s2.length; i++) {
          this.confirms.push({ index: index, input: '', position: CONFIRM_POSITION.NUMERATOR });
          index++;
        }
      }

      // フォーカス初期化（足し算、引き算、掛け算の筆算はフォーカスを一番右にする）
      const question = this.question.question;
      this.focusIndex = question.charAt(0) === 'V' && question.indexOf('/') === -1 ? this.confirms.length - 1 : 0;
    },
    $_setNextFocus(): void {
      // 右隣への移動を試みる
      const nextConfirm = this.confirms[this.focusIndex + 1];
      if (nextConfirm !== undefined && nextConfirm.input === '') {
        this.focus(this.focusIndex + 1);
        return;
      }
      // 左隣への移動を試みる
      const pervConfirm = this.confirms[this.focusIndex - 1];
      if (pervConfirm !== undefined && pervConfirm.input === '') {
        this.focus(this.focusIndex - 1);
        return;
      }
      // 先頭から順に空欄への移動を試みる
      for (let i = 0; i < this.confirms.length; i++) {
        if (this.confirms[i].input === '') {
          this.focus(i);
          return;
        }
      }
    },
    $_judge(): boolean {
      const right = this.question!.right;
      const input = this.$_getInput();
      return !this.hasEmpty && right === input;
    },
    $_calcRight(): void {
      this.questions.forEach(q => {
        if (q.right.length > 0 || q.question.length === 0) return; // 回答がある、または問題がない場合は次へ
        q.right = this.$_calc(q.question);
      });
    },
    $_calc(question: string): string {
      // 先頭が記号の場合は省く
      if (question.search(/[^0-9]/) === 0) question = question.substring(1);

      // 記号までを取得
      const index0 = question.indexOf('+');
      const index1 = question.indexOf('-');
      const index2 = question.indexOf('*');
      const index3 = question.indexOf('/');
      const index4 = question.indexOf('=');
      let operation = '';
      let s1 = '';
      let s2 = '';
      if (index0 !== -1) {
        operation = '+';
        s1 = question.substring(0, index0);
        s2 = question.substring(index0 + 1, index4);
      } else if (index1 !== -1) {
        operation = '-';
        s1 = question.substring(0, index1);
        s2 = question.substring(index1 + 1, index4);
      } else if (index2 !== -1) {
        operation = '*';
        s1 = question.substring(0, index2);
        s2 = question.substring(index2 + 1, index4);
      } else if (index3 !== -1) {
        operation = '/';
        s1 = question.substring(0, index3);
        s2 = question.substring(index3 + 1, index4);
      }

      // 整数の場合
      if (s1.indexOf('.') === -1 && s2.indexOf('.') === -1) {
        const i1 = Number(s1);
        const i2 = Number(s2);
        if (operation === '+') return String(i1 + i2);
        if (operation === '-') return String(i1 - i2);
        if (operation === '*') return String(i1 * i2);
        if (operation === '/') {
          const r0 = Math.floor(i1 / i2);
          const r1 = i1 % i2;
          if (r1 === 0) return String(r0);
          else          return r0 + '$' + r1;
        }

      // 小数の場合
      } else {
        const i1 = this.$_x10000(s1);
        const i2 = this.$_x10000(s2);
        let ret = 0;
        if      (operation === '+') ret = i1 + i2;
        else if (operation === '-') ret = i1 - i2;
        else if (operation === '*') ret = i1 * i2 / 10000;
        else if (operation === '/') ret = i1 * 10000 / i2;
        return this.$_d10000(ret);
      }

      return '';
    },
    $_x10000(str: string): number {
      const pos = str.indexOf('.');
      if (pos === -1) return Number(str) * 10000;

      // 整数部
      const str1 = str.substring(0, pos);
      // 小数部
      let   str2 = str.substring(pos + 1);
      while (str2.length < 4) str2 += '0'; // 4桁にする

      return Number(str1) * 10000 + Number(str2);
    },
    $_d10000(num: number): string {
      const i1 = Math.floor(num / 10000);
      const i2 = num % 10000;
      if (i2 === 0) return String(i1);

      // 整数部
      const str1 = String(i1);
      // 小数部
      let   str2 = String(i2);
      while (str2.length < 4) str2 = '0' + str2; // 4桁にする

      // 小数部の後ろの0を省く
      for (let i = 3; i > 0; i--) {
        if (str2.charAt(i) !== '0') break;
        str2 = str2.substring(0, i);
      }

      return str1 + '.' + str2;
    },
  }

});
